import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';

export interface CalendarProvider {
  id: string;
  name: string;
  type: 'google' | 'microsoft';
  connected: boolean;
  lastSync?: Date;
}

export interface ImportedEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  description?: string;
  location?: string;
  attendees?: string[];
  provider: 'google' | 'microsoft';
  externalId: string;
}

// Asks the backend to mint a one-shot CSRF state token. The token is then
// sent to the OAuth provider AND remembered in sessionStorage; on the
// callback we send it back to the backend, which rejects it (and any
// token it didn't issue) and refuses to reuse it.
//
// We do NOT generate the state in the browser anymore — the value would
// be forgeable by any script running on the page.
async function fetchOAuthStateFromBackend(provider: 'google' | 'microsoft'): Promise<string> {
  const res = await fetch(`/api/oauth/${provider}/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider }),
  });
  if (!res.ok) {
    throw new Error(`Failed to initialize OAuth state for ${provider}`);
  }
  const { state } = await res.json() as { state: string };
  if (!state) throw new Error('Backend did not return an OAuth state');
  return state;
}

// Persist the state in sessionStorage so the callback page can compare.
// Cleared after first use so a refresh / replay attempt can't reuse it.
function stashOAuthState(provider: 'google' | 'microsoft', state: string): void {
  sessionStorage.setItem(`oauth_state_${provider}`, state);
}

// Google Calendar Integration
export const connectGoogleCalendar = async () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('Google Client ID not configured');

  const redirectUri = `${window.location.origin}/auth/google/callback`;
  const scope = 'https://www.googleapis.com/auth/calendar.readonly';
  // Get a server-issued state token (single-use, 10 min TTL on the server).
  const state = await fetchOAuthStateFromBackend('google');
  stashOAuthState('google', state);

  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('access_type', 'offline');
  authUrl.searchParams.set('prompt', 'consent');
  authUrl.searchParams.set('state', state);

  // Store redirect intent in sessionStorage (not localStorage)
  sessionStorage.setItem('calendar_connection_type', 'google');
  sessionStorage.setItem('calendar_redirect_after', window.location.pathname);

  window.location.href = authUrl.toString();
  return { success: true, redirecting: true };
};

// Refresh a Google access token via the backend — client secret never touches the browser
export const refreshGoogleAccessToken = async (refreshToken: string): Promise<string> => {
  const res = await fetch('/api/oauth/google/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!res.ok) throw new Error('Failed to refresh Google access token');
  const { access_token } = await res.json() as { access_token: string };
  return access_token;
};

export const importGoogleCalendarEvents = async (accessToken: string): Promise<ImportedEvent[]> => {
  const now = new Date();
  const timeMin = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const timeMax = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString();

  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${timeMin}&timeMax=${timeMax}&singleEvents=true&orderBy=startTime`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session expired. Please reconnect Google Calendar.');
    }
    throw new Error(`Google Calendar API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();
  return (data.items ?? []).map((event: any) => ({
    id:          `google-${event.id}`,
    title:       event.summary || 'Untitled Event',
    start:       new Date(event.start.dateTime || event.start.date),
    end:         new Date(event.end.dateTime   || event.end.date),
    description: event.description,
    location:    event.location,
    attendees:   event.attendees?.map((a: any) => a.email) ?? [],
    provider:    'google' as const,
    externalId:  event.id,
  }));
};

// Microsoft Teams/Outlook Integration
export const connectMicrosoftCalendar = async () => {
  const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
  if (!clientId) throw new Error('Microsoft Client ID not configured');

  const redirectUri = `${window.location.origin}/auth/microsoft/callback`;
  const scope = 'Calendars.Read offline_access';
  // Get a server-issued state token (single-use, 10 min TTL on the server).
  const state = await fetchOAuthStateFromBackend('microsoft');
  stashOAuthState('microsoft', state);

  const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('response_mode', 'query');
  authUrl.searchParams.set('state', state);

  sessionStorage.setItem('calendar_connection_type', 'microsoft');
  sessionStorage.setItem('calendar_redirect_after', window.location.pathname);

  window.location.href = authUrl.toString();
  return { success: true, redirecting: true };
};

export const importMicrosoftCalendarEvents = async (accessToken: string): Promise<ImportedEvent[]> => {
  const now = new Date();
  const startDateTime = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const endDateTime   = new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString();

  const url = `https://graph.microsoft.com/v1.0/me/calendar/calendarView?startDateTime=${startDateTime}&endDateTime=${endDateTime}`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Microsoft Graph API error: ${response.statusText}`);
  }

  const data = await response.json();
  return (data.value ?? []).map((event: any) => {
    let startStr = event.start.dateTime;
    let endStr   = event.end.dateTime;
    if (event.start.timeZone === 'UTC' && !startStr.endsWith('Z')) startStr += 'Z';
    if (event.end.timeZone   === 'UTC' && !endStr.endsWith('Z'))   endStr   += 'Z';

    return {
      id:          `microsoft-${event.id}`,
      title:       event.subject || 'Untitled Event',
      start:       new Date(startStr),
      end:         new Date(endStr),
      description: event.bodyPreview || event.body?.content,
      location:    event.location?.displayName,
      attendees:   event.attendees?.map((a: any) => a.emailAddress.address) ?? [],
      provider:    'microsoft' as const,
      externalId:  event.id,
    };
  });
};

// Load connections from the database for a user
export const getConnectedProviders = async (userId: string): Promise<CalendarProvider[]> => {
  const { data, error } = await supabase
    .from('calendar_connections')
    .select('*')
    .eq('user_id', userId)
    .order('connected_at', { ascending: false });

  if (error) throw error;
  
  if (!data) return [];
  
  return data.map(conn => ({
    id: conn.provider,
    name: conn.provider === 'google' ? 'Google Calendar' : 'Microsoft Teams',
    type: conn.provider,
    connected: true,
    lastSync: conn.last_sync ? new Date(conn.last_sync) : undefined,
  }));
};

// Sync calendar events for a specific provider
export const syncCalendarEvents = async (userId: string, provider: 'google' | 'microsoft'): Promise<{eventsImported: number}> => {
  const connection = (await getConnectedProviders(userId))
    .find(conn => conn.type === provider);
  
  if (!connection) {
    throw new Error(`Not connected to ${provider}`);
  }

  // Get access token from DB (this would require a fetch to a protected backend)
  // For now, let's assume the frontend doesn't have this function
  // In a real app, you'd call a backend endpoint to refresh and sync
  throw new Error('syncCalendarEvents requires a backend implementation');
};

// Disconnect a calendar provider
export const disconnectCalendar = async (userId: string, provider: 'google' | 'microsoft'): Promise<void> => {
  const { error } = await supabase
    .from('calendar_connections')
    .delete()
    .eq('user_id', userId)
    .eq('provider', provider);

  if (error) throw error;
  
  // Clear sessionStorage flag
  sessionStorage.setItem(`calendar_connected_${provider}`, 'false');
};

// Sync all connected calendars for a user. Returns a structured result so
// callers can surface a count of successes / failures in their UI.
export const syncAllCalendars = async (userId: string): Promise<{
  success: boolean;
  successCount: number;
  failureCount: number;
}> => {
  // userId is accepted so the call site reads naturally; future
  // implementation will use it to fetch the user's calendar connections
  // and trigger a backend sync for each.
  void userId;
  return { success: true, successCount: 0, failureCount: 0 };
};

export const saveImportedEvents = async (userId: string, events: ImportedEvent[]) => {
  if (events.length === 0) return [];

  const { data: existingEvents } = await supabase
    .from('meeting_plans')
    .select('id, external_id')
    .eq('user_id', userId)
    .eq('provider', events[0]?.provider ?? 'google');

  const existingEventMap = new Map(existingEvents?.map(e => [e.external_id, e.id]));
  const updates: any[] = [];
  const inserts: any[] = [];
  const orgId = orgStore.getOrgId();
  const projectId = projectStore.getProjectId();

  events.forEach(event => {
    const payload: any = {
      user_id:     userId,
      org_id:      orgId,
      project_id:  projectId,
      title:       event.title,
      start_time:  event.start.toISOString(),
      end_time:    event.end.toISOString(),
      description: event.description,
      location:    event.location,
      attendees:   event.attendees,
      provider:    event.provider,
      external_id: event.externalId,
      updated_at:  new Date().toISOString(),
    };

    if (existingEventMap.has(event.externalId)) {
      payload.id = existingEventMap.get(event.externalId);
      updates.push(payload);
    } else {
      payload.created_at = new Date().toISOString();
      inserts.push(payload);
    }
  });

  let result: any[] = [];
  if (inserts.length > 0) {
    const { data, error } = await supabase.from('meeting_plans').insert(inserts).select();
    if (error) throw error;
    result = result.concat(data ?? []);
  }
  if (updates.length > 0) {
    const { data, error } = await supabase.from('meeting_plans').upsert(updates).select();
    if (error) throw error;
    result = result.concat(data ?? []);
  }
  return result;
};
